import { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import type { IAppliedFilter, ISearchFilter } from '../../common-types';
import { AppliedFilterChip } from '../../AppliedFilterChip';
import { getFilterName, normalizeFilterName } from '../__feature__';

interface IAppliedFiltersProps {
  appliedFilters: IAppliedFilter[];
  removeAppliedFilter: (filterName: string, option: string) => void;
  clearAppliedFilters: () => void;
  filters: ISearchFilter[];
}

export const AppliedFilters = ({
  appliedFilters,
  removeAppliedFilter,
  clearAppliedFilters,
  filters,
}: IAppliedFiltersProps) => {
  const handleDelete = (filterName: string, option: string) => () => {
    removeAppliedFilter(filterName, option);
  };

  const handleDeleteAll = useCallback(() => {
    clearAppliedFilters();
  }, [clearAppliedFilters]);

  const sortAlphabetically = (items: string[]) => {
    return items.sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
  };

  const getFilter = (filterName: string) => {
    return filters?.find(
      (p) =>
        normalizeFilterName(p.filterName) === normalizeFilterName(filterName)
    );
  };

  const applicableFilters = filters?.filter(
    (filter) => filter.dataType !== 'Boolean'
  );

  const shouldShowClearAll = appliedFilters?.some(
    (appliedFilter) =>
      appliedFilter.selectedOptions.length > 0 &&
      !!appliedFilter.filterName &&
      applicableFilters?.some(
        (filter) =>
          normalizeFilterName(filter.filterName) ===
          normalizeFilterName(appliedFilter.filterName)
      )
  );

  const getDisplayValue = (
    searchFilter: ISearchFilter | undefined,
    option: string
  ) => {
    if (
      searchFilter?.dataType === 'StringArray' &&
      Array.isArray(searchFilter?.filterValues) &&
      searchFilter.isValueTranslationRequired
    ) {
      const matchedFilterValue = searchFilter.filterValues.find(
        (f) => f.value === option
      );
      return matchedFilterValue?.translatedValue || option;
    }
    return option;
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {shouldShowClearAll && (
        <AppliedFilterChip
          label="Clear All"
          onDelete={handleDeleteAll}
          variant="filled"
          data-testid="applied-filters-clear-all"
        />
      )}

      {appliedFilters?.map((filter) => {
        const { filterName, selectedOptions } = filter;
        const searchFilter = getFilter(filterName);
        const isBooleanFilterType = searchFilter?.dataType === 'Boolean';

        if (isBooleanFilterType) {
          return null;
        }

        const filterChips = sortAlphabetically(selectedOptions).map(
          (option) => {
            let displayValue = option;
            displayValue = getDisplayValue(searchFilter, option);

            return (
              <AppliedFilterChip
                key={`${normalizeFilterName(filterName)}-${option}`}
                label={displayValue}
                onDelete={handleDelete(filterName, option)}
                variant="outlined"
                data-testid="applied-filter-chip"
                data-filter-chip-value={getFilterName(filterName)}
              />
            );
          }
        );

        return (
          selectedOptions.length > 0 &&
          filterName && (
            <Box
              key={`${normalizeFilterName(filterName)}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2">
                {getFilterName(filterName)}
              </Typography>
              {filterChips}
            </Box>
          )
        );
      })}
    </Box>
  );
};
